/**
 * Lazy Loading Components
 *
 * Centralized lazy loading for heavy components to improve initial bundle size
 * and loading performance. Components are loaded only when needed.
 */

import React, { type ComponentType, Suspense } from 'react';
import { colors } from '../utils/colors';
import type { MeetingOverlaysProps } from './overlays/MeetingOverlays';

// Loading fallback component
const ComponentLoader: React.FC<{ name?: string }> = ({ name = 'Component' }) => (
  <div className="component-loader">
    <div className="loader-spinner" />
    <span className="loader-text">Loading {name}...</span>
  </div>
);

// Higher-order component for lazy loading with error boundary
const withLazyLoading = <P extends object>(
  LazyComponent: React.LazyExoticComponent<ComponentType<P>>,
  fallbackName?: string,
) => {
  return React.forwardRef<HTMLElement, P>((props, ref) => (
    <Suspense fallback={<ComponentLoader name={fallbackName} />}>
      <LazyComponent {...props} ref={ref} />
    </Suspense>
  ));
};

// Lazy load heavy modal components
export const LazySettingsModal = withLazyLoading(
  React.lazy(() => import('./SettingsModal').then((module) => ({ default: module.SettingsModal }))),
  'Settings',
);

export const LazyPersonalMeetingsModal = withLazyLoading(
  React.lazy(() =>
    import('./PersonalMeetingsModal').then((module) => ({ default: module.PersonalMeetingsModal })),
  ),
  'Personal Meetings',
);

export const LazyScheduleMeetingModal = withLazyLoading(
  React.lazy(() =>
    import('./ScheduleMeetingModal').then((module) => ({ default: module.ScheduleMeetingModal })),
  ),
  'Schedule Meeting',
);

// Lazy load host controls page (loaded when accessing /controls/:meetingId)
const HostControlsPageLazy = React.lazy(() =>
  import('./HostControlsPage').then((module) => ({ default: module.HostControlsPage })),
);

export const LazyHostControlsPage: React.FC = () => (
  <Suspense fallback={<div style={{ position: 'fixed', inset: 0, background: colors.gray50 }} />}>
    <HostControlsPageLazy />
  </Suspense>
);

// Lazy load overlay components (loaded when meeting starts)
const MeetingOverlaysLazy = React.lazy(() =>
  import('./overlays/MeetingOverlays').then((module) => ({ default: module.MeetingOverlays })),
);

export const LazyMeetingOverlays: React.FC<MeetingOverlaysProps> = (props) => (
  <Suspense fallback={<div style={{ position: 'fixed', inset: 0, background: colors.white }} />}>
    <MeetingOverlaysLazy {...props} />
  </Suspense>
);

export const LazyTranscriptPanel = withLazyLoading(
  React.lazy(() =>
    import('./TranscriptPanel').then((module) => ({ default: module.TranscriptPanel })),
  ),
  'Transcript Panel',
);

// Lazy load authentication pages (only loaded when needed)
export const LazyLandingPage = withLazyLoading(
  React.lazy(() => import('./LandingPage').then((module) => ({ default: module.LandingPage }))),
  'Landing Page',
);

// Lazy load error pages (hopefully rarely needed)
export const LazyErrorPage = withLazyLoading(
  React.lazy(() => import('./ErrorPage').then((module) => ({ default: module.ErrorPage }))),
  'Error Page',
);

// Lazy load dial-in numbers page (standalone public page, SM-829)
export const LazyDialInNumbersPage = withLazyLoading(
  React.lazy(() =>
    import('./DialInNumbersPage/DialInNumbersPage').then((module) => ({
      default: module.DialInNumbersPage,
    })),
  ),
  'Dial-in Numbers',
);

// Preload functions for better UX
export const preloadComponents = {
  settings: () => import('./SettingsModal'),
  personalMeetings: () => import('./PersonalMeetingsModal'),
  scheduleMeeting: () => import('./ScheduleMeetingModal'),
  hostControlsPage: () => import('./HostControlsPage'),
  meetingOverlays: () => import('./overlays/MeetingOverlays'),
  transcriptPanel: () => import('./TranscriptPanel'),
  landingPage: () => import('./LandingPage'),
  errorPage: () => import('./ErrorPage'),
};

// Preload critical components on user interaction
export const preloadOnHover = (componentName: keyof typeof preloadComponents) => {
  return {
    onMouseEnter: () => preloadComponents[componentName](),
    onFocus: () => preloadComponents[componentName](),
  };
};

// Preload components based on user behavior
export const useSmartPreloading = () => {
  React.useEffect(() => {
    // Preload personal meetings modal immediately (most frequently used)
    const personalMeetingsTimer = setTimeout(() => {
      preloadComponents.personalMeetings();
    }, 3000);

    // Preload settings modal after 2 seconds (likely to be used)
    const settingsTimer = setTimeout(() => {
      preloadComponents.settings();
    }, 2000);

    // Preload schedule meeting modal after 5 seconds (common action)
    const scheduleTimer = setTimeout(() => {
      preloadComponents.scheduleMeeting();
    }, 5000);

    // Preload meeting overlays after 10 seconds (if user stays on page)
    const overlaysTimer = setTimeout(() => {
      preloadComponents.meetingOverlays();
    }, 10000);

    return () => {
      clearTimeout(personalMeetingsTimer);
      clearTimeout(settingsTimer);
      clearTimeout(scheduleTimer);
      clearTimeout(overlaysTimer);
    };
  }, []);
};

export default {
  lazySettingsModal: LazySettingsModal,
  lazyPersonalMeetingsModal: LazyPersonalMeetingsModal,
  lazyScheduleMeetingModal: LazyScheduleMeetingModal,
  lazyHostControlsPage: LazyHostControlsPage,
  lazyMeetingOverlays: LazyMeetingOverlays,
  lazyTranscriptPanel: LazyTranscriptPanel,
  lazyLandingPage: LazyLandingPage,
  lazyErrorPage: LazyErrorPage,
  preloadComponents,
  preloadOnHover,
  useSmartPreloading,
};
